/**
 * Filters the roster of people and teams a picker may offer.
 *
 * Shared by the share modal and the default-sharing setting so the two agree
 * on who is offerable and on the count behind the empty-state copy (#11877).
 */

import { useMemo, useRef } from "react";
import type { PickableTeam, PickableUser } from "./shareParts";

export interface GranteeSearchResults<U, T> {
  users: U[];
  teams: T[];
  /**
   * How many other people and teams exist at all, ignoring the query and
   * ignoring who already has access — only the viewer is excluded. It is what
   * tells "there is nobody else here" apart from "everybody already has
   * access", which is the false sentence the empty state must not print.
   */
  otherMemberCount: number;
}

export function useGranteeSearch<
  U extends PickableUser,
  T extends PickableTeam,
>({
  availableUsers,
  availableTeams,
  chosenUsers,
  chosenTeams,
  currentUserId,
  query,
  resetKey,
}: {
  availableUsers: U[];
  availableTeams: T[];
  chosenUsers: { id: string }[];
  chosenTeams: { id: string }[];
  currentUserId: string;
  query: string;
  /**
   * Drops the retained roster when it changes. The share modal stays mounted
   * while its caller swaps entities, so without this it would keep offering
   * the previous entity's people as if they were this one's. A host that
   * unmounts the picker between uses needs no key.
   */
  resetKey?: unknown;
}): GranteeSearchResults<U, T> {
  // The last roster the backend actually gave us: a refetch that comes back
  // empty must not blank the picker mid-interaction.
  const previousAvailableRef = useRef<{ users: U[]; teams: T[] } | null>(null);
  const lastResetKey = useRef(resetKey);
  if (lastResetKey.current !== resetKey) {
    lastResetKey.current = resetKey;
    previousAvailableRef.current = null;
  }

  return useMemo(() => {
    const usersToUse =
      availableUsers.length > 0
        ? availableUsers
        : (previousAvailableRef.current?.users ?? []);
    const teamsToUse =
      availableTeams.length > 0
        ? availableTeams
        : (previousAvailableRef.current?.teams ?? []);

    if (availableUsers.length > 0 || availableTeams.length > 0) {
      previousAvailableRef.current = {
        users: availableUsers,
        teams: availableTeams,
      };
    }

    const chosenUserIds = new Set(
      chosenUsers.map((u) => u.id).concat(currentUserId ? [currentUserId] : [])
    );
    const chosenTeamIds = new Set(chosenTeams.map((t) => t.id));
    const lower = query.toLowerCase().trim();

    const users = usersToUse
      .filter(
        (u) =>
          !chosenUserIds.has(u.id) &&
          (!lower ||
            u.name.toLowerCase().includes(lower) ||
            u.email?.split("@")[0]?.toLowerCase().includes(lower))
      )
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

    const teams = teamsToUse
      .filter(
        (t) =>
          !chosenTeamIds.has(t.id) &&
          (!lower || t.name.toLowerCase().includes(lower))
      )
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

    // The union of who is still offerable and who already has access: the
    // backend's share-modal roster removes everyone already shared with, so
    // counting it alone would report an empty organization.
    const memberIds = new Set<string>([
      ...usersToUse.map((u) => u.id),
      ...chosenUsers.map((u) => u.id),
    ]);
    memberIds.delete(currentUserId);
    const memberTeamIds = new Set<string>([
      ...teamsToUse.map((t) => t.id),
      ...chosenTeams.map((t) => t.id),
    ]);

    return {
      users,
      teams,
      otherMemberCount: memberIds.size + memberTeamIds.size,
    };
  }, [
    availableUsers,
    availableTeams,
    chosenUsers,
    chosenTeams,
    currentUserId,
    query,
    resetKey,
  ]);
}
